import React from 'react';
import Axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import cn from '@/utils/cn';
import QueryKeys from '@/utils/QueryKeys';
import * as Routes from '@/modules/routes';
import SPAPageHeader from '../shared/SPAPageHeader';
import DiskSpaceTable from './DiskSpaceTable';
import StatusSection from './StatusSection';

interface SystemStatusResponse {
  connection_pools: Record<string, unknown>;
  database: Record<string, unknown>;
  disk_space: { headers: string[]; rows: string[][] };
  gem_count: number;
  paperclip: Record<string, unknown>;
  redis: Record<string, unknown>;
  sidekiq: Record<string, unknown>;
  system: Record<string, unknown>;
  tls: {
    clever: Record<string, unknown> | null;
    oneroster: Record<string, unknown> | null;
  };
}

interface SkeletonSectionProps {
  header?: boolean;
  rows: number;
}

// Holds the shape of a loaded section: a heading line, then the raised frame at SPARowList's row
// height (py-3 plus one line of text-sm). `header` adds SPADataTable's header band.
const SkeletonSection = ({ header = false, rows }: SkeletonSectionProps) => (
  <div aria-hidden="true">
    <div className="tw-mb-2 tw-flex tw-h-5 tw-items-center">
      <div className="tw-h-3.5 tw-w-28 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton" />
    </div>
    <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-spa-border-secondary">
      {header && <div className="tw-h-10 tw-animate-pulse tw-bg-spa-bg-skeleton" />}
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className={cn(
            'tw-h-11 tw-animate-pulse tw-bg-spa-bg-skeleton-subtle',
            (header || index > 0) && 'tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary',
          )}
        />
      ))}
    </div>
  </div>
);

const LoadingSkeleton = () => (
  <div aria-busy="true" className="tw-flex tw-flex-col tw-gap-6">
    <SkeletonSection rows={8} />
    <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
      <SkeletonSection rows={5} />
      <SkeletonSection rows={4} />
    </div>
    <SkeletonSection rows={3} />
    <SkeletonSection header rows={3} />
  </div>
);

const StatusReport = ({ data }: { data: SystemStatusResponse }) => (
  <>
    <div className="tw-flex tw-flex-col tw-gap-6">
      <StatusSection data={data.system} title="System Info" />
      <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
        <StatusSection data={data.sidekiq} href="/sidekiq" title="Sidekiq" />
        <StatusSection data={data.redis} title="Redis" />
      </div>
      <StatusSection data={data.database} title="Database" />
      <StatusSection data={data.connection_pools} title="Connection Pools" />
      <StatusSection data={data.paperclip} title="Paperclip" />
      <DiskSpaceTable headers={data.disk_space.headers} rows={data.disk_space.rows} />
      {data.tls.oneroster && <StatusSection data={data.tls.oneroster} title="OneRoster TLS" />}
      {data.tls.clever && <StatusSection data={data.tls.clever} title="Clever TLS" />}
    </div>
    <p className="tw-mb-0 tw-mt-8 tw-text-xs tw-leading-relaxed tw-text-spa-tertiary">
      {data.gem_count} gems installed
    </p>
  </>
);

// AIDEV-NOTE: Main system status view for the SysadminSPA.
// Fetches from /status.json (Admin::HomesController#status) via React Query.
// Renders system info, sidekiq, redis, database, connection pools, disk space, TLS, and gem count.
const SystemStatus = () => {
  const { data, error, isLoading } = useQuery({
    queryFn: () => Axios.get<SystemStatusResponse>(
      Routes.status_path({ format: 'json' }),
    ).then(r => r.data),
    queryKey: [QueryKeys.SystemStatus],
  });

  const failed = !isLoading && (Boolean(error) || !data);

  return (
    <div className="tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <SPAPageHeader title="System Status" />
        <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">
          Server health, versions, and infrastructure details.
        </p>

        {isLoading && <LoadingSkeleton />}
        {failed && <p className="tw-m-0 tw-text-sm tw-text-spa-error">Failed to load system status.</p>}
        {data && !failed && <StatusReport data={data} />}
      </div>
    </div>
  );
};

export default SystemStatus;
